import React, { useState } from 'react';
import { usePlayerStore } from '../../stores/playerStore';
import { useQueueStore } from '../../stores/queueStore';
import { PlusIcon, TrashIcon, UserIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { MIN_PLAYERS_FOR_MATCH } from '../../utils/constants';

interface PlayerManagerProps {
  className?: string;
  onPlayerChange?: () => void;
}

const PlayerManager: React.FC<PlayerManagerProps> = ({ 
  className = '',
  onPlayerChange 
}) => {
  const { 
    players, 
    isLoading, 
    error, 
    addPlayer, 
    removePlayer, 
    getNextAvailableNumber,
    clearError 
  } = usePlayerStore();

  const { updateQueue, queue } = useQueueStore();

  const [showAddForm, setShowAddForm] = useState(false);
  const [newPlayerName, setNewPlayerName] = useState('');
  const [newPlayerNumber, setNewPlayerNumber] = useState<number | ''>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAddPlayer = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newPlayerName.trim()) return;

    setIsSubmitting(true);
    clearError();

    try {
      const number = newPlayerNumber || getNextAvailableNumber();
      await addPlayer(newPlayerName.trim(), number);
      
      // 更新队列以包含新玩家
      if (queue) {
        const updatedPlayers = [...players, { 
          id: 'temp', // 这会被实际的新玩家替换
          name: newPlayerName.trim(),
          number: number,
          isActive: true,
          createdAt: new Date()
        }];
        // 注意：这里应该等待addPlayer完成后再更新队列
        // 实际实现中需要重新获取玩家列表
      }

      // 重置表单
      setNewPlayerName('');
      setNewPlayerNumber('');
      setShowAddForm(false);
      
      onPlayerChange?.();
    } catch (err) {
      console.error('添加玩家失败:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemovePlayer = async (playerId: string, playerName: string) => {
    if (!window.confirm(`确定要移除玩家 "${playerName}" 吗？`)) {
      return;
    }

    clearError();

    try {
      await removePlayer(playerId);
      
      // 更新队列以移除该玩家
      if (queue) {
        const updatedPlayers = queue.players.filter(p => p.id !== playerId);
        await updateQueue(updatedPlayers);
      }

      onPlayerChange?.();
    } catch (err) {
      console.error('移除玩家失败:', err);
    }
  };

  const canRemovePlayer = (playerId: string) => {
    // 如果移除后人数少于最小比赛人数，显示警告
    return players.length > MIN_PLAYERS_FOR_MATCH;
  };

  const getPlayerWarnings = () => {
    const warnings = [];
    
    if (players.length < MIN_PLAYERS_FOR_MATCH) {
      warnings.push(`人数不足：当前 ${players.length} 人，至少需要 ${MIN_PLAYERS_FOR_MATCH} 人才能进行比赛`);
    }
    
    if (players.length === MIN_PLAYERS_FOR_MATCH) {
      warnings.push('当前刚好达到最小比赛人数，移除任何玩家都将无法进行比赛');
    }

    return warnings;
  };

  const warnings = getPlayerWarnings();

  return (
    <div className={`bg-white rounded-lg shadow-md p-6 ${className}`}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-800">人员管理</h2>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center space-x-2"
        >
          <PlusIcon className="w-5 h-5" />
          <span>添加玩家</span>
        </button>
      </div>

      {/* 错误提示 */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <div className="text-red-600 text-sm">{error}</div>
            <button
              onClick={clearError}
              className="ml-auto text-red-400 hover:text-red-600"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* 警告提示 */}
      {warnings.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div className="flex items-start space-x-2">
            <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 mt-0.5" />
            <div className="text-yellow-800 text-sm space-y-1">
              {warnings.map((warning, index) => (
                <div key={index}>{warning}</div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 添加玩家表单 */}
      {showAddForm && (
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-gray-700 mb-4">添加新玩家</h3>
          <form onSubmit={handleAddPlayer} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  姓名 *
                </label>
                <input
                  type="text"
                  value={newPlayerName}
                  onChange={(e) => setNewPlayerName(e.target.value)}
                  placeholder="输入玩家姓名"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  编号 (可选)
                </label>
                <input
                  type="number"
                  value={newPlayerNumber}
                  onChange={(e) => setNewPlayerNumber(e.target.value ? parseInt(e.target.value) : '')}
                  placeholder={`自动分配 (${getNextAvailableNumber()})`}
                  min="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            
            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={isSubmitting || !newPlayerName.trim()}
                className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
              >
                {isSubmitting ? '添加中...' : '确认添加'}
              </button>
              
              <button
                type="button"
                onClick={() => {
                  setShowAddForm(false);
                  setNewPlayerName('');
                  setNewPlayerNumber('');
                }}
                className="bg-gray-600 hover:bg-gray-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
              >
                取消
              </button>
            </div>
          </form>
        </div>
      )}

      {/* 玩家列表 */}
      <div className="space-y-3">
        <h3 className="font-semibold text-gray-700 mb-4">
          当前玩家 ({players.length})
        </h3>
        
        {isLoading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="animate-pulse bg-gray-200 h-16 rounded-lg"></div>
            ))}
          </div>
        ) : players.length > 0 ? (
          <div className="space-y-3">
            {players
              .sort((a, b) => a.number - b.number)
              .map((player) => (
                <div
                  key={player.id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200"
                >
                  <div className="flex items-center space-x-3">
                    <div className="bg-blue-500 text-white rounded-full w-10 h-10 flex items-center justify-center font-bold">
                      {player.number}
                    </div>
                    
                    <div>
                      <div className="font-semibold text-gray-800">
                        {player.name}
                      </div>
                      <div className="text-sm text-gray-600">
                        编号: {player.number} | 
                        加入时间: {new Date(player.createdAt).toLocaleDateString('zh-CN')}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <UserIcon className="w-5 h-5 text-gray-400" />
                    
                    <button
                      onClick={() => handleRemovePlayer(player.id, player.name)}
                      disabled={!canRemovePlayer(player.id)}
                      className={`p-2 rounded-lg transition-colors ${
                        canRemovePlayer(player.id)
                          ? 'text-red-600 hover:bg-red-50'
                          : 'text-gray-400 cursor-not-allowed'
                      }`}
                      title={
                        canRemovePlayer(player.id) 
                          ? '移除玩家' 
                          : '移除后人数不足，无法进行比赛'
                      }
                    >
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ))}
          </div>
        ) : (
          <div className="text-center text-gray-500 py-8">
            <UserIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-lg mb-2">暂无玩家</p>
            <p className="text-sm">点击"添加玩家"开始添加</p>
          </div>
        )}
      </div>

      {/* 操作提示 */}
      <div className="text-xs text-gray-500 mt-6 pt-4 border-t">
        <div className="space-y-1">
          <div>• 至少需要 {MIN_PLAYERS_FOR_MATCH} 个玩家才能进行比赛</div>
          <div>• 玩家编号必须唯一，留空将自动分配</div>
          <div>• 移除玩家后需要重新生成队列</div>
        </div>
      </div>
    </div>
  );
};

export default PlayerManager;